import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { appsApi } from "@/api/apps";
import { ApiError } from "@/api/types";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { EmptyState } from "@/components/empty-state";
import { formatRelative } from "@/lib/format";
import { Boxes, Copy, Plus } from "lucide-react";
import { useState } from "react";
import type { App } from "@/api/types";

export const Route = createFileRoute("/_authed/apps/")({
  component: AppsPage,
});

/** One-time reveal dialog for the plaintext signing key after app creation. */
function PlaintextRevealDialog({
  open,
  onClose,
  app,
}: {
  open: boolean;
  onClose: () => void;
  app: App | null;
}) {
  const [copied, setCopied] = useState(false);

  if (!app) return null;

  function handleCopy() {
    if (!app?.signing_key_current) return;
    void navigator.clipboard.writeText(app.signing_key_current).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>App created — save your signing key</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                This is the only time the full signing key is shown. Copy it now and store it
                securely. Your SDK uses this key to verify signed requests from deleggate.
              </p>
              <div className="flex items-center gap-2 rounded-md border bg-muted p-2 font-mono text-xs break-all">
                <span className="flex-1 select-all">{app.signing_key_current}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="shrink-0"
                  onClick={handleCopy}
                  aria-label="Copy signing key"
                >
                  <Copy size={14} />
                  {copied ? "Copied!" : "Copy"}
                </Button>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button onClick={onClose}>Done — I copied the key</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AppsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["apps"], queryFn: () => appsApi.list() });
  const apps = q.data?.data ?? [];

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [err, setErr] = useState<string | null>(null);

  // After creation, hold the new app to show its signing key.
  const [revealApp, setRevealApp] = useState<App | null>(null);
  const [revealOpen, setRevealOpen] = useState(false);

  const create = useMutation({
    mutationFn: () => appsApi.create(name, url),
    onSuccess: (newApp) => {
      setOpen(false);
      setName("");
      setUrl("");
      setErr(null);
      void qc.invalidateQueries({ queryKey: ["apps"] });
      // Show the plaintext signing key once.
      setRevealApp(newApp);
      setRevealOpen(true);
    },
    onError: (e) => {
      if (e instanceof ApiError) setErr(e.message);
      else setErr("Create failed");
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Build" title="Apps">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5 bg-brand text-primary-foreground hover:bg-brand/90">
              <Plus size={15} /> New app
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Register a new app</DialogTitle>
              <DialogDescription>
                Point deleggate at your SDK endpoint. On save we generate a signing key and
                trigger an initial sync.
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                create.mutate();
              }}
              className="space-y-3"
            >
              <div className="space-y-1.5">
                <Label htmlFor="app-name">Name</Label>
                <Input id="app-name" required value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="app-url">SDK URL</Label>
                <Input
                  id="app-url"
                  type="url"
                  required
                  placeholder="https://api.example.com/api/deleggate"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="font-mono text-[13px]"
                />
              </div>
              {err && (
                <p role="alert" className="text-sm text-fail">{err}</p>
              )}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={create.isPending} className="bg-brand text-primary-foreground hover:bg-brand/90">
                  {create.isPending ? "Creating…" : "Create app"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <Card className="overflow-hidden">
        {apps.length === 0 && !q.isLoading ? (
          <EmptyState
            icon={<Boxes size={32} />}
            title="No apps registered"
            description='Click "New app" to point deleggate at your SDK endpoint.'
            action={
              <a
                href="/docs/quickstart/"
                className="text-sm font-medium text-brand hover:underline"
              >
                Read the quickstart
              </a>
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5 font-mono text-[11px] uppercase tracking-wider">Name</TableHead>
                <TableHead className="font-mono text-[11px] uppercase tracking-wider">SDK URL</TableHead>
                <TableHead className="pr-5 text-right font-mono text-[11px] uppercase tracking-wider">Last synced</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apps.map((a) => (
                <TableRow key={a.id} className="group cursor-pointer">
                  <TableCell className="pl-5">
                    <Link to="/apps/$id" params={{ id: a.id }}
                      className="text-[13px] font-medium text-foreground group-hover:text-brand">
                      {a.name}
                    </Link>
                  </TableCell>
                  <TableCell className="font-mono text-[12px] text-muted-foreground">{a.sdk_url}</TableCell>
                  <TableCell className="tabular pr-5 text-right font-mono text-[13px] text-muted-foreground">
                    {a.last_synced_at ? formatRelative(a.last_synced_at) : "never"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <PlaintextRevealDialog
        open={revealOpen}
        onClose={() => {
          setRevealOpen(false);
          setRevealApp(null);
        }}
        app={revealApp}
      />
    </div>
  );
}
